import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatTooltipModule} from '@angular/material/tooltip';
import {detailsExpand} from '../../../../common';
import {GreydomCommonModule} from '../../../../common/modules/greydom-common.module';
import {getStatusColor, StatusColor} from '../../../../common/util/Util';
import {ProcedureCrosswalk, ProcedureCrosswalkService} from '../../../services/procedure-crosswalk.service';
import {UserAuthorisationService} from '../../../../common/services/user-authorisation.service';
import {SharedService} from '../../../../common/services/shared-service';
import {ProcedureCrosswalkDialogComponent} from './procedure-crosswalk-dialog.component';

enum TableColumns {
  Actions = 'actions',
  Rpl = 'rpl',
  Diagnosis = 'diagnosis',
  Ccsa = 'ccsa',
  Age = 'age',
  Status = 'status'
}

@Component({
  selector: 'app-procedure-crosswalk',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule,
    MatIconModule, MatInputModule, MatPaginatorModule, MatTableModule, MatTooltipModule, GreydomCommonModule],
  templateUrl: './procedure-crosswalk.component.html',
  styleUrls: ['./procedure-crosswalk.component.scss'],
  animations: [detailsExpand]
})
export class ProcedureCrosswalkComponent implements OnInit {
  readonly tableColumns = TableColumns;
  readonly canEdit: boolean;
  displayedColumns: TableColumns[];
  dataSource = new MatTableDataSource<ProcedureCrosswalk>();
  search = '';
  total = 0;
  pageIndex = 0;
  pageSize = 25;
  loading = false;
  error = '';
  expandedElement: ProcedureCrosswalk | null = null;
  private reloadPending = false;

  constructor(
    private service: ProcedureCrosswalkService,
    roles: UserAuthorisationService,
    private dialog: MatDialog,
    private sharedService: SharedService
  ) {
    this.canEdit = roles.hasRoleAdmin() || roles.hasRoleEnterpriseManager()
      || roles.hasRoleOperationsManager() || roles.hasRolePreAuthorisationManager();
    this.displayedColumns = this.canEdit
      ? Object.values(TableColumns)
      : Object.values(TableColumns).filter(column => column !== TableColumns.Actions);
  }

  ngOnInit(): void {
    this.load();
  }

  load(reset = false): void {
    if (reset) {
      this.pageIndex = 0;
    }
    if (this.loading) {
      this.reloadPending = true;
      return;
    }
    this.loading = true;
    this.error = '';
    this.service.list(this.search.trim(), this.pageIndex * this.pageSize, this.pageSize).subscribe({
      next: page => {
        this.dataSource.data = page.items;
        this.total = page.total;
        this.finishLoad();
      },
      error: () => {
        this.error = 'Could not load mappings. Please retry.';
        this.finishLoad();
      }
    });
  }

  page(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.load();
  }

  openDialog(row?: ProcedureCrosswalk): void {
    if (!this.canEdit) {
      return;
    }
    const dialogRef = this.dialog.open(ProcedureCrosswalkDialogComponent, {
      width: '70vw',
      maxWidth: '1000px',
      data: row ? {...row} : null,
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.sharedService.openSnackBar('Mapping saved successfully', 'Dismiss');
        this.load();
      }
    });
  }

  ageDescription(row: ProcedureCrosswalk): string {
    if (!row.ageUnit) {
      return 'Any age';
    }
    return `${row.minimumAge ?? 0} – ${row.maximumAge ?? 'No maximum'} ${row.ageUnit.toLowerCase()}`;
  }

  statusDescription(row: ProcedureCrosswalk): string {
    return !row.active ? 'Inactive' : row.requiresClinicalReview ? 'Clinical review required' : 'Active';
  }

  statusColor(row: ProcedureCrosswalk): StatusColor {
    return getStatusColor(!row.active ? 'error' : row.requiresClinicalReview ? 'warn' : 'success');
  }

  toggleExpanded(row: ProcedureCrosswalk): void {
    this.expandedElement = this.expandedElement === row ? null : row;
  }

  handleRowKeydown(event: KeyboardEvent, row: ProcedureCrosswalk): void {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    event.preventDefault();
    this.toggleExpanded(row);
  }

  private finishLoad(): void {
    this.loading = false;
    if (this.reloadPending) {
      this.reloadPending = false;
      this.load();
    }
  }
}
